import React from "react";
import { usePathname } from "next/navigation";

interface Breadcrumb {
  title: string;
  url: string;
}

interface BlogPostProps {
  pageTitle: string;
  pageDescription: string;
  pageImage: string;
  blogDate: string;
  authorName: string;
  authorTitle: string;
}

interface EventDetails {
  eventName: string;
  pageDescription: string;
  startDate: string;
  endDate: string;
  eventSpeaker?: string;
  location?: {
    name: string;
    streetAddress: string;
    city: string;
    postcode: string;
    country: string;
  };
  virtualEventUrl?: string;
}

interface ProductDetails {
  name: string;
  ratingValue: string;
  reviewCount: string;
}

interface OrganizationSchemaProps {
  breadcrumb?: Breadcrumb[];
  blogPost?: BlogPostProps;
  eventDetails?: EventDetails;
  productDetails?: ProductDetails;
}

const generateBreadcrumbFromPath = (pathname: string): { title: string; url: string }[] => {
  const paths = pathname.replace(/\/$/, '').split('/').filter(Boolean);

  return paths.map((path, index) => {
    const url = `https://www.targetdefense.com/${paths.slice(0, index + 1).join('/')}`;

    const title = path
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

    return { title, url };
  });
};

const SchemaMarkup: React.FC<OrganizationSchemaProps> = ({
  blogPost,
  eventDetails,
  productDetails,
}) => {
  const pathname = usePathname();
  const breadcrumbItems = generateBreadcrumbFromPath(pathname);

  return (
    <>
      {/* Organization Schema */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "TargetDefense",
          url: "https://www.targetdefense.com",
          logo: "https://www.targetdefense.com/logo.svg",
          address: {
            "@type": "PostalAddress",
            streetAddress: "3505 Lake Lynda Dr #200",
            addressLocality: "Orlando",
            postalCode: "32817",
            addressCountry: "US",
          },
          contactPoint: {
            "@type": "ContactPoint",
            telephone: "(888)6596655",
            contactType: "customer service",
            areaServed: "US",
            availableLanguage: "en-US",
          },
          sameAs: [
            "https://www.linkedin.com/company/target-defense",
          ],
        })}
      </script>

      {/* Breadcrumb Schema */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "targetdefense.com",
              item: "https://www.targetdefense.com",
            },
            ...breadcrumbItems.map((item, index) => ({
              "@type": "ListItem",
              position: index + 2,
              name: item.title,
              item: item.url,
            })),
          ],
        })}
      </script>

      {/* Blog Post Schema (if provided) */}
      {blogPost && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org/",
            "@type": "BlogPosting",
            mainEntityOfPage: {
              "@type": "WebPage",
              "@id": "https://www.targetdefense.com",
            },
            headline: blogPost.pageTitle,
            description: blogPost.pageDescription,
            image: {
              "@type": "ImageObject",
              url: blogPost.pageImage,
              width: 1440,
              height: 400,
            },
            author: {
              "@type": "Person",
              jobTitle: blogPost.authorTitle,
              name: blogPost.authorName,
            },
            publisher: {
              "@type": "Organization",
              name: "TargetDefense",
              logo: {
                "@type": "ImageObject",
                url: "https://www.targetdefense.com/logo.svg",
                width: 1920,
                height: 1080,
              },
            },
            datePublished: blogPost.blogDate,
            dateModified: blogPost.blogDate,
          })}
        </script>
      )}

      {/* Event Schema (if provided) */}
      {eventDetails && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Event",
            name: eventDetails.eventName,
            description: eventDetails.pageDescription,
            startDate: eventDetails.startDate,
            endDate: eventDetails.endDate,
            ...(eventDetails.eventSpeaker && {
              performer: {
                "@type": "Person",
                name: eventDetails.eventSpeaker,
              },
            }),
            ...(eventDetails.location && {
              location: {
                "@type": "Place",
                name: eventDetails.location.name,
                address: {
                  "@type": "PostalAddress",
                  streetAddress: eventDetails.location.streetAddress,
                  addressLocality: eventDetails.location.city,
                  postalCode: eventDetails.location.postcode,
                  addressCountry: eventDetails.location.country,
                },
              },
            }),
            ...(eventDetails.virtualEventUrl && {
              eventAttendanceMode:
                "https://schema.org/OnlineEventAttendanceMode",
              location: {
                "@type": "VirtualLocation",
                url: eventDetails.virtualEventUrl,
              },
            }),
          })}
        </script>
      )}

      {/* Add Product Schema */}
      {productDetails?.name && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            name: productDetails.name,
            ...(productDetails.ratingValue &&
              productDetails.reviewCount && {
                aggregateRating: {
                  "@type": "AggregateRating",
                  ratingValue: productDetails.ratingValue,
                  reviewCount: productDetails.reviewCount,
                },
              }),
          })}
        </script>
      )}
    </>
  );
};

export default SchemaMarkup;
